import Cookies from "js-cookie";
import { useMutation, useQueryClient } from "react-query";

const API_URL = process.env.REACT_APP_CANDIDATE_API_URL;

async function fetchJson(url: string) {
  try {
    const token = Cookies.get("token");
    if (!token) {
      throw new Error("No token found");
    }
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "x-authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка: ", error);
  }
}

async function fetchFile(
  url: string,
  method: "GET" | "POST" = "GET",
): Promise<Response> {
  try {
    const token = Cookies.get("token");
    if (!token) {
      throw new Error("No token found");
    }
    const response = await fetch(url, {
      method,
      headers: {
        "x-authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error("Ошибка: ", error);
    throw error;
  }
}

export async function getCatalogData(params: string) {
  if (params) {
    return fetchJson(`${API_URL}/candidate?${params}`);
  }
}

type UploadFileInput = {
  file: File;
  forField?: string | null;
  typeId?: string | null;
};

export async function uploadFile({
  file,
  forField = null,
  typeId = null,
}: UploadFileInput): Promise<{ fileId: string }> {
  const url = `${API_URL}/s3/upload`;

  try {
    const token = Cookies.get("token");
    if (!token) throw new Error("No token found");

    const formData = new FormData();
    formData.append("file", file);
    if (forField) formData.append("forField", forField);
    if (typeId) formData.append("typeId", typeId);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "x-authorization": `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при загрузке файла:", error);
    throw error;
  }
}

export async function getCatalogDataId(id: string) {
  if (id) {
    return fetchJson(`${API_URL}/candidate/${id}`);
  }
}

export async function getDocuments(candidateId: string) {
  return fetchJson(
    `${API_URL}/documents?candidateId=${encodeURIComponent(candidateId)}`,
  );
}

export const getPhotoUrl = (photoId: string): string => {
  return `${API_URL}/s3/view/${photoId}`;
};

export async function getDocumentFile(fileId: string): Promise<Response> {
  return fetchFile(`${API_URL}/s3/download/${fileId}`);
}

export async function getCandidateDownloadArchive(
  candidateId: string,
  sectionId: string,
): Promise<Response> {
  return fetchFile(
    `${API_URL}/candidate/${candidateId}/download?sectionId=${sectionId}`,
  );
}

export async function getCandidatePdf(candidateId: string): Promise<Response> {
  return fetchFile(`${API_URL}/pdf/candidate/${candidateId}`, "POST");
}

type UpdateStatusArgs = {
  status: string;
  candidateId: string;
};

type UpdateAuditStatusArgs = {
  auditStatus: string;
  candidateId: string;
};

const updateStatusRequest = async (
  endpoint: "status" | "audit-status",
  field: "status" | "auditStatus",
  value: string,
  candidateId: string,
) => {
  const url = `${API_URL}/candidate/${endpoint}`;

  const token = Cookies.get("token");
  if (!token) throw new Error("No token found");

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "x-authorization": `Bearer ${token}`,
    },
    body: JSON.stringify({ [field]: value, candidateId }),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const text = await response.text();
  if (text === "ok" || text === "OK") {
    return { success: true };
  }

  try {
    return JSON.parse(text);
  } catch {
    return { success: true };
  }
};

const updateCandidateStatus = ({ status, candidateId }: UpdateStatusArgs) =>
  updateStatusRequest("status", "status", status, candidateId);

const updateCandidateAuditStatus = ({
  auditStatus,
  candidateId,
}: UpdateAuditStatusArgs) =>
  updateStatusRequest("audit-status", "auditStatus", auditStatus, candidateId);

const useStatusMutation = <TArgs>(
  mutationFn: (args: TArgs) => Promise<unknown>,
) => {
  const queryClient = useQueryClient();

  return useMutation(mutationFn, {
    onSuccess: () => {
      console.log("Mutation success, invalidating queries...");
      queryClient.invalidateQueries(["getCatalogData"]).then(() => {
        console.log("Queries invalidated");
      });
    },
    onError: (error) => {
      console.error("Update error:", error);
    },
  });
};

export const useUpdateCandidateStatus = () =>
  useStatusMutation(updateCandidateStatus);

export const useUpdateCandidateAuditStatus = () =>
  useStatusMutation(updateCandidateAuditStatus);
